import type { Address } from "viem";
import { validArtworkBaseUrl } from "../config/nft-artwork";
import { ELYSIUM, sameIdentity } from "../../../config/elysium";

const MAX_TOKEN_URI_CHARS = 1_500_000;
const MAX_METADATA_BYTES = 512_000;
const MAX_SVG_BYTES = 1_000_000;

export type CanonicalMetadataStatus = "PENDING_RANDOMNESS" | "RANDOMNESS_READY" | "FINALIZED";
export type NftArtworkKind = "paid" | "task" | "referral";

export type CanonicalNftMedia = Readonly<{
  tokenUri: string;
  name: string;
  description: string;
  rewardStatus: CanonicalMetadataStatus;
  rewardBps: number | null;
  rewardRate: number | null;
  rewardDenominator: 10_000 | 1_000_000;
  imageUrl: string;
  artworkKind: NftArtworkKind | null;
}>;

export type CanonicalMediaExpectation = Readonly<{
  tokenId?: string;
  status: CanonicalMetadataStatus;
  rewardBps: number | null;
  rewardPolicyVersion?: string | null;
  rewardRate?: number | null;
  rewardDenominator?: number | null;
  sourceToken?: Address;
  tierUsd?: number;
  chainId?: number;
  collectionAddress?: Address;
  artworkBaseUrl?: string | null;
}>;

export function parseCanonicalNftTokenUri(tokenUri: string,
  expected?: CanonicalMediaExpectation): CanonicalNftMedia {
  if (tokenUri.length === 0 || tokenUri.length > MAX_TOKEN_URI_CHARS) throw new Error("INVALID_TOKEN_URI_SIZE");
  const metadataText = decodeTextDataUri(tokenUri, "application/json", MAX_METADATA_BYTES);
  let parsed: unknown;
  try { parsed = JSON.parse(metadataText); }
  catch { throw new Error("INVALID_TOKEN_METADATA_JSON"); }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) throw new Error("INVALID_TOKEN_METADATA");
  const metadata = parsed as Record<string, unknown>;
  if (typeof metadata.name !== "string" || metadata.name.length === 0 || metadata.name.length > 256 ||
    typeof metadata.description !== "string" || metadata.description.length === 0 || metadata.description.length > 2_048 ||
    typeof metadata.image !== "string") throw new Error("INVALID_TOKEN_METADATA_FIELDS");
  if (metadata.rewardStatus !== "PENDING_RANDOMNESS" && metadata.rewardStatus !== "RANDOMNESS_READY" &&
    metadata.rewardStatus !== "FINALIZED") throw new Error("INVALID_TOKEN_METADATA_STATUS");
  const rewardStatus = metadata.rewardStatus;
  const { rewardRate, rewardBps, rewardDenominator } = validateCanonicalReward(metadata, rewardStatus, expected);
  if (expected) assertExpectedMetadata(metadata, rewardStatus, rewardBps, expected);
  let artworkKind: NftArtworkKind | null = null;
  if (metadata.image.startsWith("data:")) {
    const svg = decodeTextDataUri(metadata.image, "image/svg+xml", MAX_SVG_BYTES);
    assertPassiveSvg(svg);
    if (expected?.rewardPolicyVersion === "2") artworkKind = "paid";
  } else {
    artworkKind = assertCanonicalPng(metadata.image, rewardRate, expected);
  }
  return Object.freeze({ tokenUri, name: metadata.name, description: metadata.description,
    rewardStatus, rewardBps, rewardRate, rewardDenominator,
    imageUrl: metadata.image, artworkKind });
}

/** Validate reward units and state independently of SVG/PNG representation. */
function validateCanonicalReward(metadata: Record<string, unknown>, rewardStatus: CanonicalMetadataStatus,
  expected?: CanonicalMediaExpectation): Pick<CanonicalNftMedia, "rewardRate" | "rewardBps" | "rewardDenominator"> {
  const v2 = expected?.rewardPolicyVersion === "2";
  const rewardDenominator = v2 ? 1_000_000 : 10_000;
  if (v2 && (expected.chainId !== ELYSIUM.chainId || !sameIdentity(expected.collectionAddress, ELYSIUM.collection) ||
    ![10, 100, 500, 1000, 2000, 5000].includes(expected.tierUsd ?? -1) ||
    expected.rewardDenominator !== 1_000_000 || metadata.rewardDenominator !== 1_000_000 ||
    metadata.rewardBps != null) || !v2 && (metadata.rewardPpm != null ||
    metadata.rewardDenominator !== undefined && metadata.rewardDenominator !== 10_000))
    throw new Error("TOKEN_METADATA_REWARD_DENOMINATOR_MISMATCH");
  const rewardRate = v2 ? metadata.rewardPpm : metadata.rewardBps;
  if (rewardRate !== null && (!Number.isInteger(rewardRate) || typeof rewardRate !== "number" ||
    rewardRate < 0 || rewardRate > (v2 ? 20_000 : 10_000))) throw new Error("INVALID_TOKEN_METADATA_REWARD");
  const rewardBps = v2 ? null : rewardRate as number | null;
  if ((rewardStatus === "FINALIZED") !== (typeof rewardRate === "number")) {
    throw new Error("TOKEN_METADATA_REWARD_STATUS_MISMATCH");
  }
  if (expected) {
    const expectedRate = expected.rewardRate !== undefined ? expected.rewardRate : expected.rewardBps;
    if (rewardRate !== expectedRate || expected.rewardDenominator != null && expected.rewardDenominator !== rewardDenominator)
      throw new Error("TOKEN_METADATA_MIRROR_MISMATCH");
  }
  return { rewardRate: rewardRate as number | null, rewardBps, rewardDenominator };
}

/** The path contains the already validated native reward rate (BPS or PPM). */
function assertCanonicalPng(image: string, rewardRate: number | null, expected?: CanonicalMediaExpectation): NftArtworkKind {
  const base = expected?.artworkBaseUrl;
  if (!expected || !base || !validArtworkBaseUrl(base)) throw new Error("UNTRUSTED_NFT_ARTWORK_URL");
  const { chainId, collectionAddress, tokenId, tierUsd, status } = expected;
  if (!Number.isSafeInteger(chainId) || !chainId || chainId <= 0 ||
    !collectionAddress || !/^0x[0-9a-fA-F]{40}$/.test(collectionAddress) ||
    !tokenId || !/^(0|[1-9][0-9]{0,77})$/.test(tokenId) || BigInt(tokenId) >= 1n << 256n ||
    tierUsd === undefined || !Number.isInteger(tierUsd) || tierUsd < 0 || !expected.sourceToken) {
    throw new Error("INVALID_NFT_ARTWORK_IDENTITY");
  }
  const identity = `${chainId}/${collectionAddress.toLowerCase()}/${tokenId}`;
  const display = `${tierUsd}/${status}/${rewardRate ?? "pending"}.png`;
  const candidates = new Map<string, NftArtworkKind>();
  // Match complete canonical paths; overlapping tiers 100/500 do not identify NFT kind.
  // Paid PPM artwork has a distinct v3 namespace; never accept legacy BPS paths.
  if (expected.rewardPolicyVersion === "2") {
    candidates.set(`${base}/v3/${identity}/paid/${display}`, "paid");
  } else {
    if ([10, 100, 500, 1000, 2000, 5000].includes(tierUsd)) candidates.set(`${base}/v1/${identity}/${display}`, "paid");
    if (tierUsd === 0) candidates.set(`${base}/v2/${identity}/task/${display}`, "task");
    if ([50, 100, 500].includes(tierUsd)) candidates.set(`${base}/v2/${identity}/referral/${display}`, "referral");
  }
  if (candidates.size === 0) throw new Error("INVALID_NFT_ARTWORK_IDENTITY");
  const kind = candidates.get(image);
  if (!kind) throw new Error("NFT_ARTWORK_URL_MISMATCH");
  return kind;
}

function assertExpectedMetadata(metadata: Record<string, unknown>, status: CanonicalMetadataStatus,
  rewardBps: number | null, expected: CanonicalMediaExpectation): void {
  if (status !== expected.status || rewardBps !== expected.rewardBps) {
    throw new Error("TOKEN_METADATA_MIRROR_MISMATCH");
  }
  const elysium = expected.chainId === ELYSIUM.chainId;
  if (elysium && !sameIdentity(expected.collectionAddress, ELYSIUM.collection)) throw new Error("TOKEN_METADATA_IDENTITY_MISMATCH");
  if (expected.tokenId !== undefined && metadata.name !== `${elysium ? "Elysium" : "Pons"} Reward NFT #${expected.tokenId}`) {
    throw new Error("TOKEN_METADATA_IDENTITY_MISMATCH");
  }
  if (expected.sourceToken) {
    const value = attributeValue(metadata.attributes, "Source Token Address");
    if (typeof value !== "string" || value.toLowerCase() !== expected.sourceToken.toLowerCase()) {
      throw new Error("TOKEN_METADATA_SOURCE_MISMATCH");
    }
  }
  if (expected.tierUsd !== undefined) {
    const value = attributeValue(metadata.attributes, "Tier USD");
    if (value !== expected.tierUsd) throw new Error("TOKEN_METADATA_TIER_MISMATCH");
  }
}

function attributeValue(attributes: unknown, traitType: string): unknown {
  if (!Array.isArray(attributes)) return undefined;
  const matches = attributes.filter((candidate) => candidate && typeof candidate === "object" &&
    (candidate as Record<string, unknown>).trait_type === traitType);
  return matches.length === 1 ? (matches[0] as Record<string, unknown>).value : undefined;
}

function decodeTextDataUri(uri: string, expectedMime: string, maxBytes: number): string {
  const comma = uri.indexOf(",");
  if (comma < 0 || comma > 160) throw new Error("INVALID_DATA_URI");
  const header = uri.slice(5, comma);
  if (!uri.startsWith("data:") || header.length === 0) throw new Error("INVALID_DATA_URI");
  const parts = header.split(";");
  const mime = parts.shift()?.toLowerCase();
  if (mime !== expectedMime) throw new Error("UNSUPPORTED_DATA_URI_MEDIA_TYPE");
  let base64 = false;
  for (const rawPart of parts) {
    const part = rawPart.toLowerCase();
    if (part === "base64") {
      if (base64) throw new Error("INVALID_DATA_URI_ENCODING");
      base64 = true;
    } else if (part !== "charset=utf-8" && part !== "charset=utf8") {
      throw new Error("UNSUPPORTED_DATA_URI_PARAMETER");
    }
  }
  const payload = uri.slice(comma + 1);
  let bytes: Uint8Array;
  if (base64) {
    if (payload.length === 0 || payload.length % 4 !== 0 || !/^[A-Za-z0-9+/]*={0,2}$/.test(payload)) {
      throw new Error("INVALID_DATA_URI_BASE64");
    }
    let decoded: string;
    try { decoded = atob(payload); }
    catch { throw new Error("INVALID_DATA_URI_BASE64"); }
    bytes = Uint8Array.from(decoded, (character) => character.charCodeAt(0));
  } else {
    let decoded: string;
    try { decoded = decodeURIComponent(payload); }
    catch { throw new Error("INVALID_DATA_URI_PERCENT_ENCODING"); }
    bytes = new TextEncoder().encode(decoded);
  }
  if (bytes.byteLength === 0 || bytes.byteLength > maxBytes) throw new Error("DATA_URI_SIZE_LIMIT");
  try { return new TextDecoder("utf-8", { fatal: true }).decode(bytes); }
  catch { throw new Error("INVALID_DATA_URI_UTF8"); }
}

function assertPassiveSvg(svg: string): void {
  if (!/^\s*<svg\b/i.test(svg) || !/<\/svg>\s*$/i.test(svg)) throw new Error("INVALID_CANONICAL_SVG");
  // SVG is rendered as an <img>, but reject active or externally-referencing
  // constructs as defense in depth. The immutable contract output only uses
  // svg/rect/g/text elements and needs none of these features.
  if (/<\s*(?:script|foreignObject|iframe|object|embed|image|use|style|link|audio|video)\b/i.test(svg) ||
    /\bon[a-z]+\s*=/i.test(svg) || /\b(?:href|src)\s*=/i.test(svg) || /\burl\s*\(/i.test(svg) ||
    /@import|javascript:|data:text\/html|<!DOCTYPE|<!ENTITY|<\?/i.test(svg)) {
    throw new Error("ACTIVE_CANONICAL_SVG_REJECTED");
  }
}
